import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";

const AffidavitFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    deponentFullName: "",
    deponentFatherOrHusband: "",
    deponentAge: "",
    deponentGender: "",
    deponentOccupation: "",
    deponentContact: "",
    deponentAddress: "",
    deponentEmail: "",
    affidavitType: "",
    affidavitTypeOther: "",
    affidavitPurpose: "",
    affidavitTitle: "",
    factsAndCircumstances: "",
    statement1: "",
    statement2: "",
    statement3: "",
    statement4: "",
    statement5: "",
    legalBasis: "",
    supportingDocuments: "",
    previousAffidavits: "",
    urgency: "",
    jurisdiction: "",
    notaryRequired: "",
    witnessName1: "",
    witnessAddress1: "",
    witnessName2: "",
    witnessAddress2: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Deponent Details:
Full Name: ${formData.deponentFullName}
Father's/Husband's Name: ${formData.deponentFatherOrHusband}
Age: ${formData.deponentAge}
Gender: ${formData.deponentGender}
Occupation: ${formData.deponentOccupation}
Contact: ${formData.deponentContact}
Email: ${formData.deponentEmail}
Address: ${formData.deponentAddress}

📋 Affidavit Details:
Type: ${formData.affidavitTypeOther || formData.affidavitType}
Title: ${formData.affidavitTitle}
Purpose: ${formData.affidavitPurpose}

📝 Affidavit Statements:
Facts and Circumstances: ${formData.factsAndCircumstances}
Statement 1: ${formData.statement1}
Statement 2: ${formData.statement2}
Statement 3: ${formData.statement3}
Statement 4: ${formData.statement4}
Statement 5: ${formData.statement5}

⚖️ Legal Information:
Legal Basis: ${formData.legalBasis}
Supporting Documents: ${formData.supportingDocuments}
Previous Affidavits: ${formData.previousAffidavits}
Urgency: ${formData.urgency}
Jurisdiction: ${formData.jurisdiction}
Notary Required: ${formData.notaryRequired}

👥 Witness Details:
Witness 1: ${formData.witnessName1} - ${formData.witnessAddress1}
Witness 2: ${formData.witnessName2} - ${formData.witnessAddress2}
`.trim();

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
        draftType: "affidavit",
        entityName: formData.deponentFullName,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Deponent Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="deponentFullName"
            value={formData.deponentFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="deponentFatherOrHusband"
            value={formData.deponentFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="deponentAge"
            value={formData.deponentAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="deponentGender"
            value={formData.deponentGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="deponentOccupation"
            value={formData.deponentOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="deponentContact"
            value={formData.deponentContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="deponentEmail"
            value={formData.deponentEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="deponentAddress"
            value={formData.deponentAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Affidavit Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Affidavit Type *</label>
          <select
            name="affidavitType"
            value={formData.affidavitType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Affidavit Type</option>
            <option value="Identity Affidavit">Identity Affidavit</option>
            <option value="Age Affidavit">Age Affidavit</option>
            <option value="Income Affidavit">Income Affidavit</option>
            <option value="Residence Affidavit">Residence Affidavit</option>
            <option value="Marriage Affidavit">Marriage Affidavit</option>
            <option value="Divorce Affidavit">Divorce Affidavit</option>
            <option value="Death Affidavit">Death Affidavit</option>
            <option value="Birth Affidavit">Birth Affidavit</option>
            <option value="Name Change Affidavit">Name Change Affidavit</option>
            <option value="Property Affidavit">Property Affidavit</option>
            <option value="Court Affidavit">Court Affidavit</option>
            <option value="Employment Affidavit">Employment Affidavit</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.affidavitType === "Other" && (
            <>
              <label>Specify Affidavit Type *</label>
              <input
                type="text"
                name="affidavitTypeOther"
                value={formData.affidavitTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Affidavit Title *</label>
          <input
            type="text"
            name="affidavitTitle"
            value={formData.affidavitTitle}
            onChange={handleInputChange}
            placeholder="Title or heading for this affidavit"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Purpose of Affidavit *</label>
          <textarea
            name="affidavitPurpose"
            value={formData.affidavitPurpose}
            onChange={handleInputChange}
            rows={3}
            placeholder="Purpose and reason for creating this affidavit..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Facts and Circumstances *</label>
          <textarea
            name="factsAndCircumstances"
            value={formData.factsAndCircumstances}
            onChange={handleInputChange}
            rows={4}
            placeholder="Background facts and circumstances relevant to this affidavit..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📝 Affidavit Statements
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Statement 1 *</label>
          <textarea
            name="statement1"
            value={formData.statement1}
            onChange={handleInputChange}
            rows={3}
            placeholder="First statement or fact you want to affirm..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Statement 2</label>
          <textarea
            name="statement2"
            value={formData.statement2}
            onChange={handleInputChange}
            rows={3}
            placeholder="Second statement or fact you want to affirm..."
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Statement 3</label>
          <textarea
            name="statement3"
            value={formData.statement3}
            onChange={handleInputChange}
            rows={3}
            placeholder="Third statement or fact you want to affirm..."
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Statement 4</label>
          <textarea
            name="statement4"
            value={formData.statement4}
            onChange={handleInputChange}
            rows={3}
            placeholder="Fourth statement or fact you want to affirm..."
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Statement 5</label>
          <textarea
            name="statement5"
            value={formData.statement5}
            onChange={handleInputChange}
            rows={3}
            placeholder="Fifth statement or fact you want to affirm..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          ⚖️ Legal Information & Witnesses
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Legal Basis/Reference</label>
            <textarea
              name="legalBasis"
              value={formData.legalBasis}
              onChange={handleInputChange}
              rows={2}
              placeholder="Legal provisions or acts under which this affidavit is made..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Supporting Documents</label>
            <textarea
              name="supportingDocuments"
              value={formData.supportingDocuments}
              onChange={handleInputChange}
              rows={2}
              placeholder="List of documents attached as evidence..."
            />
          </div>
          <div className="form-page-col">
            <label>Previous Affidavits</label>
            <textarea
              name="previousAffidavits"
              value={formData.previousAffidavits}
              onChange={handleInputChange}
              rows={2}
              placeholder="Details of any previous affidavits made for similar matters..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Urgency Level</label>
            <select
              name="urgency"
              value={formData.urgency}
              onChange={handleInputChange}
            >
              <option value="">Select Urgency</option>
              <option value="Immediate">Immediate</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Normal">Normal</option>
            </select>
          </div>
          <div className="form-page-col">
            <label>Jurisdiction</label>
            <input
              type="text"
              name="jurisdiction"
              value={formData.jurisdiction}
              onChange={handleInputChange}
              placeholder="Court or authority jurisdiction"
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Notary Required</label>
            <select
              name="notaryRequired"
              value={formData.notaryRequired}
              onChange={handleInputChange}
            >
              <option value="">Select Option</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Witness 1 Name</label>
            <input
              type="text"
              name="witnessName1"
              value={formData.witnessName1}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-page-col">
            <label>Witness 1 Address</label>
            <textarea
              name="witnessAddress1"
              value={formData.witnessAddress1}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Witness 2 Name</label>
            <input
              type="text"
              name="witnessName2"
              value={formData.witnessName2}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-page-col">
            <label>Witness 2 Address</label>
            <textarea
              name="witnessAddress2"
              value={formData.witnessAddress2}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>
      </div>
      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I solemnly affirm that the statements made in this affidavit are true to the best of my knowledge *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Affidavit Document</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional affidavit</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Affidavit Document
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffidavitFormPage;
